import { Search } from 'lucide-react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from '../ui/utils';

type SearchFieldSize = 'sm' | 'md';

type SearchFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  label?: string;
  labelSrOnly?: boolean;
  filter?: boolean;
  icon?: ReactNode;
  trailing?: ReactNode;
  kbd?: string;
  size?: SearchFieldSize;
};

const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      className,
      label = 'Buscar',
      labelSrOnly = true,
      filter = false,
      icon,
      trailing,
      kbd,
      size = 'md',
      id,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const iconSize = size === 'sm' ? 15 : 16;
    const trailingNode = trailing ?? (kbd ? <kbd>{kbd}</kbd> : null);
    const fieldClassName = cn(
      'nx-search-field',
      size === 'sm' && 'nx-search-field--sm',
      filter && 'nx-search-field--filter',
    );

    const control = (
      <>
        {icon ?? <Search size={iconSize} strokeWidth={2} aria-hidden />}
        <input ref={ref} id={inputId} type="search" {...props} />
        {trailingNode}
      </>
    );

    if (filter) {
      return (
        <label className={cn('nx-filter-field', 'nx-filter-field--search', className)} htmlFor={inputId}>
          <span>{label}</span>
          <div className={fieldClassName}>{control}</div>
        </label>
      );
    }

    return (
      <div className={cn(fieldClassName, className)}>
        <label htmlFor={inputId} className={labelSrOnly ? 'sr-only' : undefined}>
          {label}
        </label>
        {control}
      </div>
    );
  },
);

SearchField.displayName = 'SearchField';

export { SearchField };
export type { SearchFieldProps, SearchFieldSize };
